import React, { useState, useEffect } from 'react';
import Step1Inputs from './steps/Step1Inputs';
import Step2Review from './steps/Step2Review';
import AddClientModal from './modals/AddClientModal';
import ProcessSheetTable from './ProcessSheetTable';
import LogoToggle from './LogoToggle';
import { getBondByLabel, computeSheet } from '../../utils/calculations';

const EMPTY_FORM = {
    batchNumber:    '',
    gritCode:       '',
    bondType:       '',
    concentration:  '',
    mouldWidth:     '',
    mouldLength:    '',
    abrasiveLayer:  '',
    slotWidth:      '',
    numberOfMoulds: '',
    sticksPerMould: '',
    stoneSize:      '',
    stonesPerStick: '',
};

/**
 * ProcessSheetCreate
 * Props:
 *   onBack        — () => void
 *   existingSheet — sheet object (edit mode) or null (create mode)
 */
const ProcessSheetCreate = ({ onBack, existingSheet = null }) => {
    const isEditMode = !!existingSheet;

    const [formData, setFormData]             = useState(EMPTY_FORM);
    const [step, setStep]                     = useState(1);
    const [processData, setProcessData]       = useState(null);
    const [computed, setComputed]             = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [logo, setLogo]                     = useState('axis');
    const [savedSheet, setSavedSheet]         = useState(null);
    const [verified, setVerified]             = useState(false);
    const [addClientOpen, setAddClientOpen]   = useState(false);
    const [addClientInitial, setAddClientInitial] = useState('');

    // ── Pre-populate in edit mode ─────────────────────────────────
    useEffect(() => {
        if (!existingSheet) return;
        const inp = existingSheet.inputs || {};

        const populated = {
            batchNumber:    inp.batchNumber    || '',
            gritCode:       inp.gritCode       || '',
            bondType:       inp.bondType       || '',
            concentration:  String(inp.concentration  ?? ''),
            mouldWidth:     String(inp.mouldWidth     ?? ''),
            mouldLength:    String(inp.mouldLength    ?? ''),
            abrasiveLayer:  String(inp.abrasiveLayer  ?? ''),
            slotWidth:      String(inp.slotWidth      ?? ''),
            numberOfMoulds: String(inp.numberOfMoulds ?? ''),
            sticksPerMould: String(inp.sticksPerMould ?? ''),
            stoneSize:      inp.stoneSize      || '',
            stonesPerStick: String(inp.stonesPerStick ?? ''),
        };
        setFormData(populated);

        if (existingSheet.clientId) {
            setSelectedClient({ _id: existingSheet.clientId, name: existingSheet.clientName });
        }

        setSavedSheet(existingSheet);

        // Auto-compute preview on load in edit mode
        const bond = getBondByLabel(inp.bondType || '');
        if (bond) {
            const result = computeSheet(populated, bond);
            setProcessData(populated);
            setComputed(result);
        }
    // run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const bond = getBondByLabel(formData.bondType);
        if (!bond) return;

        const result = computeSheet(formData, bond);
        setProcessData({ ...formData });
        setComputed(result);
        setVerified(false);
        setStep(2);
    };

    const handleReset = () => {
        setFormData(EMPTY_FORM);
        setStep(1);
        setProcessData(null);
        setComputed(null);
        setSelectedClient(null);
        setSavedSheet(null);
        setVerified(false);
    };

    const handlePrint = () => {
        window.print();
    };

    const tableInputs = processData
        ? { ...processData, customerName: selectedClient?.name || '' }
        : null;

    return (
        <>
            <AddClientModal
                isOpen={addClientOpen}
                initialName={addClientInitial}
                onSuccess={(client) => { setSelectedClient(client); setAddClientOpen(false); }}
                onClose={() => setAddClientOpen(false)}
            />

            {/* ── Header ────────────────────────────────────────────── */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <button onClick={onBack}
                        className="text-gray-400 hover:text-gray-700 text-sm transition-colors">
                        ← All Sheets
                    </button>
                    <h1 className="text-xl font-bold text-gray-800">
                        {isEditMode ? 'Edit Sheet' : 'New Process Sheet'}
                    </h1>
                    {savedSheet && (
                        <span className="text-xs font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            {savedSheet.sheetNumber}
                        </span>
                    )}
                </div>
                {!isEditMode && (
                    <button onClick={handleReset}
                        className="text-xs text-gray-400 hover:text-gray-600 border border-gray-200 px-3 py-1.5 rounded-lg transition-colors">
                        Reset
                    </button>
                )}
            </div>

            <div className="flex flex-col lg:flex-row gap-5 min-h-[600px]">

                {/* ── Sidebar ───────────────────────────────────────── */}
                <div className="w-full lg:w-[380px] flex-shrink-0">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">

                        {/* Step indicator */}
                        <div className="flex items-center gap-2 mb-4">
                            {[{ n: 1, label: 'Inputs' }, { n: 2, label: 'Review' }].map(({ n, label }, idx) => (
                                <React.Fragment key={n}>
                                    {idx > 0 && <div className={`flex-1 h-px transition-colors ${step >= n ? 'bg-gray-900' : 'bg-gray-200'}`} />}
                                    <div className={`flex items-center gap-1.5 text-xs font-semibold transition-colors ${step === n ? 'text-gray-900' : 'text-gray-400'}`}>
                                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs transition-colors ${step >= n ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                            {n}
                                        </span>
                                        {label}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>

                        {step === 1 && (
                            <Step1Inputs
                                formData={formData}
                                onChange={handleChange}
                                onSubmit={handleSubmit}
                                selectedClient={selectedClient}
                                onClientSelect={setSelectedClient}
                                onClientClear={() => setSelectedClient(null)}
                                onAddNewClient={(name) => { setAddClientInitial(name); setAddClientOpen(true); }}
                            />
                        )}

                        {step === 2 && computed && (
                            <Step2Review
                                computed={computed}
                                processData={processData}
                                selectedClient={selectedClient}
                                existingSheetId={isEditMode ? existingSheet._id : null}
                                verified={verified}
                                onVerify={() => setVerified(true)}
                                onUnverify={() => setVerified(false)}
                                onSaved={setSavedSheet}
                                onBack={() => setStep(1)}
                            />
                        )}
                    </div>
                </div>

                {/* ── Sheet preview ─────────────────────────────────── */}
                <div className="flex-1">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        {tableInputs && computed ? (
                            <>
                                <div className="flex items-center justify-between mb-4 no-print">
                                    <LogoToggle value={logo} onChange={setLogo} />
                                    <span className="text-xs text-gray-400">
                                        Vol: {computed.cupVolumeInCubicCm} cm³ · Stones: {computed.totalStones}
                                    </span>
                                    <button onClick={handlePrint}
                                        className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-xs font-medium transition-colors">
                                        🖨 Print
                                    </button>
                                </div>
                                <ProcessSheetTable
                                    inputs={tableInputs}
                                    computed={computed}
                                    sheetNumber={savedSheet?.sheetNumber || null}
                                    logo={logo}
                                />
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full py-20 text-center text-gray-400">
                                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4 text-2xl">📋</div>
                                <p className="font-medium text-gray-500 mb-1">Sheet preview will appear here</p>
                                <p className="text-sm">
                                    {isEditMode
                                        ? 'Update inputs and click Generate Sheet to refresh the preview'
                                        : 'Select a client and fill in the inputs, then click Generate Sheet'}
                                </p>
                                <p className="text-xs mt-3 text-gray-300">Size format: 3x3x110</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProcessSheetCreate;